import {
  Button,
  Card,
  Input,
  Select,
  Space,
  Spin,
  Switch,
  Row,
  Col,
  DatePicker,
  Tooltip,
  Tag,
  theme,
  message,
} from 'antd';
import { Field } from 'react-final-form';
import SelectTimezones from '../SelectTimezones';
import { useLocation, useParams } from 'react-router-dom';
import { useCompanyData, useGetPrice } from '../../../../../../../Hooks/TT_ELD/Companies';
import { Form } from 'react-final-form';
import { sendEvent } from '../../../../../../../Utils/analytics';
import { companies } from '../../../../../../../API/TT_ELD/Users/company';
import { getUserFromLocalStorage } from '../../../../../../../Utils/sidebar';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CheckCircleTwoTone, CloseCircleTwoTone, CopyOutlined } from '@ant-design/icons';
import { isWhiteLabelAdmin } from '../../../../../../../Utils/whiteLabel';
import { hasRteAccess, useProjectData } from '../../../../../../../Hooks/useProjectData';
import { eldTypeLabel } from '../../../../../../../Utils/eldTypeLabels';

dayjs.extend(utc);

type params = {
  readonly id: any;
};
type MyObjectType = {
  [key: string | number]: any;
};

const validate = (values: any) => {
  const errors: any = {};
  if (!values.host) {
  } else if (!values.host.startsWith('http://') && !values.host.startsWith('https://')) {
    errors.host = 'URL must start with http:// or https://';
  }
  if (values.expireCompanyDate?.trim() === '') {
    errors.expireCompanyDate = 'Please enter your  expireCompanyDate ';
  }
  return errors;
};

const MainField = () => {
  const user = getUserFromLocalStorage();

  const isWhiteLabel = isWhiteLabelAdmin();

  const { projectData } = useProjectData();

  const role = user?.role;

  const { token } = theme.useToken();

  const successColor = token.colorSuccess;
  const errorColor = token.colorError;

  const { id } = useParams<params>();
  const { data, refetch }: MyObjectType = useCompanyData(id);

  const getPrices = useGetPrice(id);

  const showPrice = isWhiteLabel && hasRteAccess(projectData.projectName);

  const isIntegrated = Boolean(data?.firstRequestTime && data?.lastRequestTime);

  const location = useLocation();
  let disable = location.pathname === `/sales/disabled-companies/${id}`;

  const copyToClipboard = async (value: string | number | null | undefined) => {
    if (value === undefined || value === null || value === '') {
      message.warning('Public ID is empty');
      return;
    }

    const text = String(value);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      message.success('Public ID copied');
    } catch {
      message.error('Failed to copy Public ID');
    }
  };

  const onSubmit = async (values: any) => {
    values = {
      ...values,
      chatId: values.chatId ? values.chatId : null,
      terminal_address: values.terminal_address ? values.terminal_address : null,
      address: values.address ? values.address : null,
      state: values.state ? values.state : null,
      post_code: values.post_code ? values.post_code : null,
      host: values.host ? values.host : null,
      disableCompanyDate: values.disableCompanyDate ? values.disableCompanyDate : null,
      contractUnpaidAt: values.contractUnpaidAt ? values.contractUnpaidAt : null,
      note: values.note ? values.note.trim() : null,
      expireCompanyDate:
        disable && values.expireCompanyDate
          ? values.expireCompanyDate.trim()
          : disable
            ? null
            : values.expireCompanyDate,
    };
    const body: MyObjectType = {};

    if (values.sub_upd_cal_to_next_month !== undefined) {
      body.config = {
        sub_upd_cal_to_next_month: values.sub_upd_cal_to_next_month,
      };
    }
    if (values.enable_weekly_ifta !== undefined) {
      body.config = {
        enable_weekly_ifta: values.enable_weekly_ifta,
      };
    }

    delete values.sub_upd_cal_to_next_month;
    delete values.weekly_ifta;
    delete values.price;
    delete values.subscriptionId;
    delete values.quantity;
    let key: string | number;
    let key2: string | number;
    for (key in values) {
      if (typeof data[key] === 'object' && !Array.isArray(data[key]) && data[key] != null) {
        for (key2 in values[key]) {
          if (
            typeof values[key][key2] === 'object' &&
            !Array.isArray(values[key][key2]) &&
            values[key][key2] != null
          ) {
            if (JSON.stringify(values[key][key2]) != JSON.stringify(data[key][key2])) {
              body[key] = values[key];
              break;
            }
          } else if (data[key][key2] != values[key][key2]) {
            body[key] = values[key];
            break;
          }
        }
      } else if (data[key] != values[key]) {
        body[key] = values[key];
      }
    }

    sendEvent(`Company page`, `Company  Changed `, `Company  page Submit`, 1);

    if (Object.keys(body).length === 0) {
      message.warning('No changes detected');
      return;
    }
    if (Object.keys(body).length > 0) {
      // Company type is read-only here (changed only via the ELD Migration tab), so always
      // send the server's current value rather than the one loaded with this form
      if (body.config && 'eldType' in body.config) {
        body.config = { ...body.config, eldType: await companies.latestEldType(id) };
      }
      await companies.companyPatchData(body, id);
      await refetch();
    }
  };

  return (
    <>
      <Spin size="large" spinning={!data}>
        <Card className="mainPage">
          {data && (
            <Form
              onSubmit={onSubmit}
              validate={validate}
              initialValues={{
                ...data,
                priceId: data?.priceId,
              }}
              render={({ handleSubmit, values }) => (
                <form onSubmit={handleSubmit}>
                  <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={24} md={12} lg={8}>
                        <label>Name</label>
                        <Field
                          name="name"
                          render={({ input }: { input: any }) => (
                            <Input placeholder="Name" type="text" {...input} />
                          )}
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={8}>
                        <label>Public ID</label>
                        <Field
                          name="publicId"
                          render={({ input }: { input: any }) => (
                            <Space.Compact style={{ width: '100%' }}>
                              <Input disabled type="text" {...input} style={{ flex: 1 }} />
                              <Tooltip title="Copy Public ID">
                                <Button
                                  icon={<CopyOutlined />}
                                  onClick={() => copyToClipboard(input.value)}
                                  disabled={input.value === undefined || input.value === null || input.value === ''}
                                />
                              </Tooltip>
                            </Space.Compact>
                          )}
                        />
                      </Col>
                      {!isWhiteLabel && (
                        <Col
                          xs={24}
                          sm={24}
                          md={12}
                          lg={8}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {isIntegrated ? (
                              <>
                                <CheckCircleTwoTone twoToneColor={successColor} />
                                <span
                                  style={{
                                    color: successColor,
                                    fontWeight: 500,
                                    fontSize: 16,
                                  }}
                                >
                                  Terminal integrated
                                </span>
                              </>
                            ) : (
                              <>
                                <CloseCircleTwoTone twoToneColor={errorColor} />
                                <span
                                  style={{
                                    color: errorColor,
                                    fontWeight: 500,
                                    fontSize: 16,
                                  }}
                                >
                                  Terminal not integrated
                                </span>
                              </>
                            )}
                          </div>
                        </Col>
                      )}
                      {showPrice && (
                        <Col xs={24} sm={24} md={12} lg={8}>
                          <label>Price</label>
                          <Field
                            name="priceId"
                            render={({ input }) => (
                              <Select
                                {...input}
                                placeholder="price"
                                style={{ width: '100%' }}
                                onChange={(value) => input.onChange(value)}
                                value={input.value}
                              >
                                {getPrices?.data?.prices.map((data: any) => (
                                  <Select.Option key={data.id} value={data.id}>
                                    {data.unit_amount / 100}
                                  </Select.Option>
                                ))}
                              </Select>
                            )}
                          />
                        </Col>
                      )}
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={24} md={12} lg={8}>
                        <label>USDOT</label>
                        <Field
                          name="usdot"
                          render={({ input }: { input: any }) => (
                            <Input placeholder="USDOT" type="text" {...input} />
                          )}
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={8}>
                        <label>Timezone</label>
                        <Field
                          name="tz"
                          initialValue={data?.tz}
                          render={({ input }: { input: any }) => <SelectTimezones input={input} />}
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      {!isWhiteLabel && (
                        <>
                          <Col xs={24} sm={24} md={12} lg={8}>
                            <label>Company type</label>
                            {/* Read-only: switching must go through the ELD Migration tab so
                                drivers, logs and the subscription are migrated too */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 32 }}>
                              <Tag color={data?.config?.eldType === 'custom' ? 'blue' : 'default'}>
                                {eldTypeLabel(data?.config?.eldType)}
                              </Tag>
                              <span style={{ fontSize: 12, opacity: 0.65 }}>
                                Change it in the ELD Migration tab
                              </span>
                            </div>
                          </Col>
                          <Col xs={24} sm={24} md={12} lg={8}>
                            <label>Host</label>
                            <Field
                              name="host"
                              render={({ input, meta }: { input: any; meta: any }) => (
                                <div>
                                  <Input placeholder="host" type="text" {...input} />
                                  {(meta.error || meta.submitError) && meta.touched && (
                                    <span style={{ color: 'red' }}>
                                      {meta.error || meta.submitError}
                                    </span>
                                  )}
                                </div>
                              )}
                            />
                          </Col>
                        </>
                      )}
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={24} md={12} lg={8}>
                        <label>Terminal address</label>
                        <Field
                          name="terminal_address"
                          render={({ input }: { input: any }) => (
                            <Input placeholder="terminal address" type="text" {...input} />
                          )}
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={8}>
                        <label>Address</label>
                        <Field
                          name="address"
                          render={({ input }: { input: any }) => (
                            <Input placeholder="address" type="text" {...input} />
                          )}
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      {!isWhiteLabel && (
                        <>
                          <Col xs={24} sm={24} md={12} lg={8}>
                            <label>State</label>
                            <Field
                              name="state"
                              render={({ input }: { input: any }) => (
                                <Input placeholder="state" type="text" {...input} />
                              )}
                            />
                          </Col>
                          <Col xs={24} sm={24} md={12} lg={8}>
                            <label>Postcode</label>
                            <Field
                              name="post_code"
                              render={({ input }: { input: any }) => (
                                <Input placeholder="postcode" type="text" {...input} />
                              )}
                            />
                          </Col>
                        </>
                      )}
                    </Row>
                    <Row gutter={[16, 16]}>
                      {!isWhiteLabel && (
                        <Col xs={24} sm={24} md={12} lg={8}>
                          <label>Owner ID</label>
                          <Field
                            name="ownerId"
                            render={({ input }: { input: any }) => (
                              <Input placeholder="owner id" type="text" {...input} />
                            )}
                          />
                        </Col>
                      )}
                    </Row>
                    <Row gutter={[16, 16]}>
                      {!isWhiteLabel && disable && (role === 'developer' || role === 'manager') && (
                        <Col xs={24} sm={24} md={12} lg={8}>
                          <label>Disabled at</label>
                          <Field
                            name="disableCompanyDate"
                            render={({ input }: { input: any }) => {
                              const isDisabled = role !== 'developer' && role !== 'manager';

                              const value = input.value ? dayjs.utc(input.value) : null;

                              return (
                                <DatePicker
                                  disabled={isDisabled}
                                  placeholder="Disable Company Date"
                                  value={value}
                                  onChange={(date) =>
                                    input.onChange(
                                      date ? dayjs(date).utc(true).toISOString() : null,
                                    )
                                  }
                                  showTime={{ format: 'HH:mm' }}
                                  format="MMMM DD, YYYY HH:mm"
                                  style={{ width: '100%' }}
                                />
                              );
                            }}
                          />
                        </Col>
                      )}
                      {!isWhiteLabel && disable && (role === 'developer' || role === 'manager') && (
                        <Col xs={24} sm={24} md={12} lg={8}>
                          <label>Expired at</label>
                          <Field
                            name="expireCompanyDate"
                            render={({ input, meta }: { input: any; meta: any }) => {
                              const isDisabled = role !== 'developer' && role !== 'manager';

                              const value = input.value ? dayjs.utc(input.value) : null;
                              return (
                                <>
                                  <DatePicker
                                    disabled={isDisabled}
                                    placeholder="Expire Company Date"
                                    value={value}
                                    showTime={{ format: 'HH:mm' }}
                                    format="MMMM DD, YYYY HH:mm"
                                    onChange={(date) =>
                                      input.onChange(
                                        date ? dayjs(date).utc(true).toISOString() : null,
                                      )
                                    }
                                    style={{ width: '100%' }}
                                  />

                                  {(meta.error || meta.submitError) && meta.touched && (
                                    <span style={{ color: 'red' }}>
                                      {meta.error || meta.submitError}
                                    </span>
                                  )}
                                </>
                              );
                            }}
                          />
                        </Col>
                      )}
                    </Row>
                    <Row gutter={[16, 16]}>
                      {!isWhiteLabel && disable && (role === 'developer' || role === 'manager') && (
                        <Col xs={24} sm={24} md={12} lg={8}>
                          <label>Contract Unpaid At</label>
                          <Field
                            name="contractUnpaidAt"
                            render={({ input }: { input: any }) => {
                              const isDisabled = role !== 'developer' && role !== 'manager';
                              const value = input.value ? dayjs.utc(input.value) : null;

                              return (
                                <DatePicker
                                  disabled={isDisabled}
                                  placeholder="Contract Unpaid Date"
                                  value={value}
                                  showTime={{ format: 'HH:mm' }}
                                  format="MMMM DD, YYYY HH:mm"
                                  onChange={(date) =>
                                    input.onChange(
                                      date ? dayjs(date).utc(true).toISOString() : null,
                                    )
                                  }
                                  style={{ width: '100%' }}
                                />
                              );
                            }}
                          />
                        </Col>
                      )}
                      {disable && (role === 'developer' || role === 'manager') && (
                        <Col xs={24} sm={24} md={12} lg={8}>
                          <label>Note</label>
                          <Field
                            name="Note"
                            render={({ input }: { input: any }) => (
                              <Input placeholder="note" type="text" {...input} />
                            )}
                          />
                        </Col>
                      )}
                    </Row>
                    <Row gutter={[16, 16]}>
                      {!isWhiteLabel && (
                        <Col xs={24} sm={24} md={12} lg={4} style={{ display: 'flex' }}>
                          <Field
                            name="is_active"
                            render={({ input }: { input: any }) => {
                              const isDisabled = role === 'support' ? input.value !== true : false;
                              return (
                                <div>
                                  <Switch
                                    disabled={isDisabled}
                                    checked={input.value}
                                    onChange={input.onChange}
                                  />
                                </div>
                              );
                            }}
                          />
                          <label style={{ marginLeft: '15px' }}>Is Active</label>
                        </Col>
                      )}

                      {!isWhiteLabel && (
                        <Col xs={24} sm={24} md={12} lg={4} style={{ display: 'flex' }}>
                          <Field
                            name="payForDriver"
                            render={({ input }: { input: any }) => (
                              <div>
                                <Switch checked={input.value} onChange={input.onChange} />
                              </div>
                            )}
                          />
                          <label style={{ marginLeft: '15px' }}>Pay For Driver</label>
                        </Col>
                      )}

                      {!isWhiteLabel && (
                        <Col xs={24} sm={24} md={12} lg={4} style={{ display: 'flex' }}>
                          <Field
                            name="isBanned"
                            render={({ input }: { input: any }) => (
                              <div>
                                <Switch checked={input.value} onChange={input.onChange} />
                              </div>
                            )}
                          />
                          <label style={{ marginLeft: '15px' }}>Is Banned</label>
                        </Col>
                      )}

                      {!isWhiteLabel && (
                        <Col xs={24} sm={24} md={12} lg={4} style={{ display: 'flex' }}>
                          <Field
                            name="config.enable_weekly_ifta"
                            render={({ input }: { input: any }) => (
                              <div>
                                <Switch checked={input.value} onChange={input.onChange} />
                              </div>
                            )}
                          />
                          <label style={{ marginLeft: '15px' }}>Enable Weekly IFTA</label>
                        </Col>
                      )}
                      {!isWhiteLabel && (
                        <Col xs={24} sm={24} md={12} lg={4} style={{ display: 'flex' }}>
                          <Field
                            name="config.sub_upd_cal_to_next_month"
                            render={({ input }: { input: any }) => (
                              <div>
                                <Switch checked={input.value} onChange={input.onChange} />
                              </div>
                            )}
                          />
                          <label style={{ marginLeft: '15px' }}>
                            Subscription updates calculation to next month
                          </label>
                        </Col>
                      )}
                    </Row>
                    <Button style={{ marginTop: '20px' }} type="primary" htmlType="submit">
                      Submit
                    </Button>
                  </Space>
                </form>
              )}
            />
          )}
        </Card>
      </Spin>
    </>
  );
};

export default MainField;
